/**
 * Scaffolder Field Extension Registration
 *
 * This file creates the scaffolder field extensions that can be added to
 * the Backstage ScaffolderPage.
 *
 * Copy this file to: packages/app/src/scaffolder/extensions.tsx
 */

import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import {
  GithubRepoPicker,
  GithubFilePicker,
  GithubRepoVariablePicker,
  GithubTeamPicker,
  FlatArrayField,
  TerraformVariablesEditor,
  GithubTerraformModuleEditor,
  type GithubRepoPickerOptions,
  type GithubFilePickerOptions,
  type GithubRepoVariablePickerOptions,
  type GithubTeamPickerOptions,
  type FlatArrayFieldOptions,
  type TerraformVariablesEditorOptions,
  type GithubTerraformModuleEditorOptions,
  TenantConfigurator,
  type TenantConfiguratorOptions,
} from './field-extensions';

/**
 * GithubRepoPickerExtension
 *
 * A dropdown picker for selecting GitHub repositories matching a pattern.
 *
 * Usage in template.yaml:
 * ```yaml
 * ui:field: GithubRepoPicker
 * ui:options:
 *   owner: badal-io
 *   pattern: 'repo-bu-*'
 * ```
 */
export const GithubRepoPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension<string, GithubRepoPickerOptions>({
    name: 'GithubRepoPicker',
    component: GithubRepoPicker,
  }),
);

/**
 * GithubFilePickerExtension
 *
 * A dropdown picker for selecting files from a GitHub repository directory.
 *
 * Usage in template.yaml:
 * ```yaml
 * ui:field: GithubFilePicker
 * ui:options:
 *   repoParam: business_unit_repo
 *   path: terraform/tenants
 *   pattern: 'tenant_*.tf'
 * ```
 */
export const GithubFilePickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension<string, GithubFilePickerOptions>({
    name: 'GithubFilePicker',
    component: GithubFilePicker,
  }),
);

/**
 * GithubRepoVariablePickerExtension
 *
 * A dropdown picker that reads a GitHub repository variable containing
 * comma-separated values and displays them as selectable options.
 *
 * Usage in template.yaml:
 * ```yaml
 * ui:field: GithubRepoVariablePicker
 * ui:options:
 *   repoParam: business_unit_repo
 *   variableName: ENVIRONMENTS
 *   separator: ','           # Optional, defaults to ','
 *   multiple: false          # Optional, allows multiple selection
 * ```
 */
export const GithubRepoVariablePickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension<string | string[], GithubRepoVariablePickerOptions>({
    name: 'GithubRepoVariablePicker',
    component: GithubRepoVariablePicker,
  }),
);

/**
 * GithubTeamPickerExtension
 *
 * A dropdown picker for selecting GitHub teams from an organization.
 *
 * Usage in template.yaml:
 * ```yaml
 * ui:field: GithubTeamPicker
 * ui:options:
 *   owner: badal-io
 *   pattern: '*'         # Optional, filter teams by name pattern
 * ```
 */
export const GithubTeamPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension<string, GithubTeamPickerOptions>({
    name: 'GithubTeamPicker',
    component: GithubTeamPicker,
  }),
);

/**
 * FlatArrayFieldExtension
 *
 * A flattened array field renderer that eliminates nested containers,
 * providing a cleaner UI for complex array structures.
 *
 * Usage in template.yaml:
 * ```yaml
 * ui:field: FlatArrayField
 * ui:options:
 *   itemLayout: 'grid'      # 'grid', 'stacked', or 'inline'
 *   collapsible: true       # Enable collapsible items
 *   showItemBorders: true   # Show subtle item borders
 *   columns: 2              # Grid columns (2, 3, or 4)
 *   addButtonText: 'Add'    # Custom add button text
 *   itemTitle: 'Item'       # Title for each item
 * ```
 */
export const FlatArrayFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension<any[], FlatArrayFieldOptions>({
    name: 'FlatArrayField',
    component: FlatArrayField,
  }),
);

/**
 * TerraformVariablesEditorExtension
 *
 * A dynamic form editor that parses Terraform variable definitions and
 * generates appropriate input fields for each variable type.
 *
 * Usage in template.yaml:
 * ```yaml
 * ui:field: TerraformVariablesEditor
 * ui:options:
 *   terraformPath: '/path/to/terraform/module'
 *   # OR use a parameter reference:
 *   terraformPathParam: 'repoPath'
 *   tfvarsPath: '/path/to/terraform.tfvars'  # Optional
 *   tfvarsPathParam: 'tfvarsPath'            # Optional
 *   hideSensitive: false                      # Hide sensitive variables
 *   collapseObjects: false                    # Collapse object editors by default
 *   showDescriptions: true                    # Show variable descriptions
 * ```
 */
export const TerraformVariablesEditorExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension<Record<string, unknown>, TerraformVariablesEditorOptions>({
    name: 'TerraformVariablesEditor',
    component: TerraformVariablesEditor,
  }),
);

/**
 * GithubTerraformModuleEditorExtension
 *
 * A dynamic form editor that fetches a Terraform file from GitHub, parses
 * module blocks, retrieves the module's variables from the registry, and
 * generates appropriate input fields for each variable type.
 *
 * This solves the problem of editing Terraform module configurations
 * directly from GitHub repositories without needing local file access.
 *
 * Usage in template.yaml:
 * ```yaml
 * ui:field: GithubTerraformModuleEditor
 * ui:options:
 *   owner: badal-io                    # GitHub organization
 *   repoParam: business_unit_repo      # Reference to repo picker field
 *   fileParam: tenant_file             # Reference to file picker field
 *   path: terraform/tenants            # Optional: directory path prefix
 *   moduleIndex: 0                     # Optional: which module to edit if multiple (default: 0)
 *   showDescriptions: true             # Optional: show variable descriptions
 *   hideSensitive: false               # Optional: hide sensitive variables
 * ```
 */
export const GithubTerraformModuleEditorExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension<Record<string, unknown>, GithubTerraformModuleEditorOptions>({
    name: 'GithubTerraformModuleEditor',
    component: GithubTerraformModuleEditor,
  }),
);

export const TenantConfiguratorExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension<object, TenantConfiguratorOptions>({
    name: 'TenantConfigurator',
    component: TenantConfigurator,
    validation: (value, validation) => {
      if (!value || typeof value !== 'object') return;
      const config = value as Record<string, unknown>;
      const variant = (config._variant as string) || 'default';

      // Required in all modes
      if (!config.tenant_name) {
        validation.addError('Tenant Name is required');
      }
      if (!config.description) {
        validation.addError('Description is required');
      }
      if (!config.business_unit_repo) {
        validation.addError('Business Unit is required');
      }

      // Required in default and edit (not sandbox)
      if (variant !== 'sandbox') {
        if (!config.purpose) {
          validation.addError('Purpose is required');
        }
        if (!config.owner_name) {
          validation.addError('Owner Name is required');
        }
        if (!config.owner_email) {
          validation.addError('Owner Email is required');
        }
      }

      // Teams required in default only
      if (variant === 'default') {
        const teams = config.teams as Array<{ name: string }> | undefined;
        if (!teams || teams.filter(t => t.name).length === 0) {
          validation.addError('At least one GitHub team is required');
        }
      }

      // Budget minimums (not in sandbox variant where budget isn't shown)
      if (variant !== 'sandbox') {
        const lifetimeBudget = config.lifetime_budget as number;
        const monthlyBudget = config.budget_amount as number;
        if (lifetimeBudget != null && lifetimeBudget <= 100) {
          validation.addError('Lifetime budget must be greater than $100');
        }
        if (monthlyBudget != null && monthlyBudget <= 10) {
          validation.addError('Monthly budget must be greater than $10');
        }
      }
    },
  }),
);

